import axios from "axios";
import { endpoints } from "@/configs/Settings";
import { useAuth } from "@/utils/useAuth";
import { RefreshTokenGoogle } from "../RefreshTokenGoogle";

const GOOGLE_FIT_OAUTH_CLIENT_KEY = "googleFitOAuthClient";

export type GoogleFitOAuthClient = "web" | "mobile";

export function getGoogleFitOAuthClient(): GoogleFitOAuthClient | null {
  const value = localStorage.getItem(GOOGLE_FIT_OAUTH_CLIENT_KEY);
  if (value === "web" || value === "mobile") return value;
  return null;
}

export function setGoogleFitOAuthClient(client: GoogleFitOAuthClient): void {
  localStorage.setItem(GOOGLE_FIT_OAUTH_CLIENT_KEY, client);
}

export function clearGoogleFitOAuthClient(): void {
  localStorage.removeItem(GOOGLE_FIT_OAUTH_CLIENT_KEY);
}

function webFitClient() {
  return {
    clientId:
      import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_ID ||
      import.meta.env.VITE_GOOGLE_CLIENT_ID ||
      endpoints.GOOGLE_CLIENT_ID,
    clientSecret:
      import.meta.env.VITE_GOOGLE_HEALTHCARE_CLIENT_SECRET ||
      import.meta.env.VITE_GOOGLE_CLIENT_SECRET ||
      endpoints.GOOGLE_CLIENT_SECRET,
  };
}

function mobileFitClient() {
  return {
    clientId:
      import.meta.env.VITE_GOOGLE_CLIENT_ID_MOBILE ||
      import.meta.env.VITE_GOOGLE_CLIENT_ID ||
      "",
  };
}

/**
 * Renova o access token do Google Fit/Health.
 *
 * O refresh token fica “preso” ao client que gerou o vínculo:
 * - Web → client Healthcare Web + secret
 * - App Android → client mobile (público, sem secret)
 *
 * Quem vinculou no mobile e abre o Web quebrava com 401 ao renovar
 * só com o client Web. Aqui tentamos o client preferido e, se falhar,
 * o outro — sem pedir reconexão.
 */
export async function refreshGoogleFitAccessToken(refreshToken: string) {
  const preferred = getGoogleFitOAuthClient();
  const web = webFitClient();
  const mobile = mobileFitClient();

  const attempts: Array<{
    client: GoogleFitOAuthClient;
    run: () => ReturnType<typeof RefreshTokenGoogle>;
  }> = [];

  const pushWeb = () =>
    attempts.push({
      client: "web",
      run: () =>
        RefreshTokenGoogle(refreshToken, {
          clientId: web.clientId,
          clientSecret: web.clientSecret,
        }),
    });

  const pushMobile = () =>
    attempts.push({
      client: "mobile",
      run: () =>
        RefreshTokenGoogle(refreshToken, {
          clientId: mobile.clientId,
          publicClient: true,
        }),
    });

  if (preferred === "mobile") {
    pushMobile();
    pushWeb();
  } else {
    // Preferência web ou desconhecida: tenta Web e depois mobile
    pushWeb();
    pushMobile();
  }

  let lastError: unknown;
  for (const attempt of attempts) {
    try {
      const tokens = await attempt.run();
      setGoogleFitOAuthClient(attempt.client);
      return tokens;
    } catch (error) {
      lastError = error;
      // 400/401: client errado para este refresh token — tenta o outro
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status && status !== 400 && status !== 401) {
        throw error;
      }
    }
  }

  console.error("Falha ao renovar token Google Fit (web e mobile):", lastError);
  throw lastError instanceof Error
    ? lastError
    : new Error("Falha ao renovar token do Google Fit");
}
